import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Feature key to route/page mapping
export interface FeatureNavConfig {
  featureKey: string;
  routes: string[];
  navItem?: {
    path: string;
    icon: string;
    labelKey: string;
    defaultLabel: string;
  };
  priority: number; // Lower = higher priority for default page
}

// Configuration mapping feature keys to their navigation items and routes
export const FEATURE_NAV_CONFIG: FeatureNavConfig[] = [
  {
    featureKey: 'dashboard',
    routes: ['/dashboard'],
    navItem: { path: '/dashboard', icon: 'BarChart3', labelKey: 'nav.dashboard', defaultLabel: 'Dashboard' },
    priority: 1,
  },
  {
    featureKey: 'contract_management',
    routes: ['/contracts', '/categories', '/contract-types', '/currencies', '/contacts', '/groups', '/services'],
    navItem: { path: '/contracts', icon: 'FileText', labelKey: 'nav.contracts', defaultLabel: 'Contracts' },
    priority: 2,
  },
  {
    featureKey: 'sales_analytics',
    routes: ['/sales-analytics'],
    navItem: { path: '/sales-analytics', icon: 'TrendingUp', labelKey: 'nav.salesAnalytics', defaultLabel: 'Sales Analytics' },
    priority: 3,
  },
  {
    featureKey: 'cost_optimization',
    routes: ['/cost-optimization'],
    navItem: { path: '/cost-optimization', icon: 'TrendingDown', labelKey: 'nav.costOptimization', defaultLabel: 'Cost Optimization' },
    priority: 4,
  },
  {
    featureKey: 'advanced_dashboard',
    routes: ['/dashboard'],
    priority: 5,
  },
  {
    featureKey: 'erp_integration',
    routes: ['/settings/integrations', '/erp-integration/tripletex/setup', '/erp-integration/finago/setup', '/erp-integration/finago/callback'],
    navItem: { path: '/settings/integrations', icon: 'Link', labelKey: 'nav.erpIntegration', defaultLabel: 'ERP Integration' },
    priority: 6,
  },
  {
    // Expense detection routes require BOTH erp_integration AND contract_management
    // This is a virtual config - routes are only added if BOTH features are enabled
    featureKey: 'erp_expense_detection',
    routes: ['/erp-integration/tripletex/expenses', '/erp-integration/finago/expenses', '/erp-integration/pending-contracts'],
    priority: 100, // Not used for nav, just for route matching
  },
  {
    featureKey: 'document_extraction',
    routes: ['/contracts/extract', '/documents/extraction'],
    navItem: { path: '/documents/extraction', icon: 'FileSearch', labelKey: 'nav.documentExtraction', defaultLabel: 'Document Extraction' },
    priority: 7,
  },
  {
    featureKey: 'custom_fields',
    routes: ['/settings/custom-fields'],
    navItem: { path: '/settings/custom-fields', icon: 'Sliders', labelKey: 'nav.customFields', defaultLabel: 'Custom Fields' },
    priority: 8,
  },
  {
    featureKey: 'calendar_view',
    routes: ['/calendar'],
    navItem: { path: '/calendar', icon: 'Calendar', labelKey: 'nav.calendarView', defaultLabel: 'Calendar' },
    priority: 9,
  },
  {
    featureKey: 'reminders',
    routes: ['/reminders'],
    priority: 10,
  },
  {
    featureKey: 'api_read',
    routes: ['/api-portal'],
    navItem: { path: '/api-portal', icon: 'Code', labelKey: 'nav.apiPortal', defaultLabel: 'API Portal' },
    priority: 11,
  },
  {
    featureKey: 'api_write',
    routes: ['/api-portal'],
    priority: 12,
  },
  {
    featureKey: 'data_health',
    routes: ['/data-health'],
    navItem: { path: '/data-health', icon: 'HeartPulse', labelKey: 'nav.dataHealth', defaultLabel: 'Data Health' },
    priority: 13,
  },
];

interface CustomPlanInfo {
  isCustomPlan: boolean;
  featureKeys: string[];
}

async function fetchCustomPlanInfo(): Promise<CustomPlanInfo> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    return { isCustomPlan: false, featureKeys: [] };
  }

  const { data: subscription } = await supabase
    .from('user_subscriptions')
    .select('is_custom_plan, custom_features')
    .eq('user_id', userData.user.id)
    .eq('status', 'active')
    .maybeSingle();

  if (!subscription || !subscription.is_custom_plan) {
    return { isCustomPlan: false, featureKeys: [] };
  }

  // Support both key names for backwards compatibility
  const customFeatures = subscription.custom_features as { feature_keys?: string[], enabled_features?: string[] } | null;
  const featureKeys = customFeatures?.enabled_features || customFeatures?.feature_keys || [];
  return {
    isCustomPlan: true,
    featureKeys,
  };
}

export function useCustomPlanInfo() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['custom-plan-info'],
    queryFn: fetchCustomPlanInfo,
    enabled: isAuthenticated,
    staleTime: 1000 * 30, // 30 seconds - shorter for more responsive updates
    refetchOnWindowFocus: true,
    refetchOnMount: 'always', // Always refetch on mount to get latest status
  });
}

export function useCustomPlanNavigation() {
  const { data: customPlanInfo, isLoading } = useCustomPlanInfo();

  const navigationConfig = useMemo(() => {
    if (!customPlanInfo?.isCustomPlan) {
      return null; // Not a custom plan, use regular navigation
    }

    const enabledFeatures = customPlanInfo.featureKeys;

    // Filter nav configs to only include enabled features
    const enabledNavItems = FEATURE_NAV_CONFIG
      .filter(config => enabledFeatures.includes(config.featureKey) && config.navItem)
      .sort((a, b) => a.priority - b.priority);

    // Determine default landing page
    const defaultPage = enabledNavItems.length > 0 
      ? enabledNavItems[0].navItem?.path || '/dashboard'
      : '/settings/subscription'; // Fallback if no features enabled

    // Get all allowed routes
    const allowedRoutes = enabledNavItems.flatMap(config => config.routes);

    // Always allow settings and documentation
    allowedRoutes.push('/settings', '/settings/subscription', '/documentation', '/profile');

    return {
      isCustomPlan: true,
      enabledFeatures,
      navItems: enabledNavItems.map(config => config.navItem!),
      defaultPage,
      allowedRoutes,
      hasFeature: (featureKey: string) => enabledFeatures.includes(featureKey),
    };
  }, [customPlanInfo]);

  return {
    isLoading,
    isCustomPlan: customPlanInfo?.isCustomPlan || false,
    navigationConfig,
    featureKeys: customPlanInfo?.featureKeys || [],
  };
}

// Helper to check if a route is allowed for custom plan user
export function isRouteAllowedForCustomPlan(
  route: string, 
  featureKeys: string[]
): boolean {
  // Always allow these routes
  const alwaysAllowed = ['/settings', '/documentation', '/profile', '/settings/subscription'];
  if (alwaysAllowed.some(allowed => route.startsWith(allowed))) {
    return true;
  }

  // Special handling: expense detection routes require BOTH erp_integration AND contract_management
  const expenseDetectionRoutes = [
    '/erp-integration/tripletex/expenses',
    '/erp-integration/finago/expenses',
    '/erp-integration/pending-contracts'
  ];
  if (expenseDetectionRoutes.some(r => route.startsWith(r))) {
    return featureKeys.includes('erp_integration') && featureKeys.includes('contract_management');
  }

  // Check if route matches any enabled feature's routes
  for (const config of FEATURE_NAV_CONFIG) {
    if (featureKeys.includes(config.featureKey)) {
      if (config.routes.some(r => route.startsWith(r))) {
        return true;
      }
    }
  }

  return false;
}

// Get the default landing page for a custom plan based on enabled features
export function getCustomPlanDefaultPage(featureKeys: string[]): string {
  const sortedConfigs = FEATURE_NAV_CONFIG
    .filter(config => featureKeys.includes(config.featureKey) && config.navItem)
    .sort((a, b) => a.priority - b.priority);

  if (sortedConfigs.length > 0 && sortedConfigs[0].navItem) {
    return sortedConfigs[0].navItem.path;
  }

  return '/settings/subscription'; // Fallback
}
